import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { featureDisabledMessage, type ThreadMessageDto } from '@aiai/shared';
import { deleteThread, fetchProject, fetchThread, updateThread } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { projectNav, threadsHref } from '../lib/projectNav';
import { maybeNotifyPmMessage } from '../lib/threadNotifications';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { useCanonicalProjectRef } from '../hooks/useCanonicalProjectRef';
import { useCapabilities } from '../hooks/useCapabilities';
import { useFeature } from '../hooks/useEntitlements';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';
import { useThreadSocket } from '../hooks/useThreadSocket';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Callout } from '../components/Callout';
import { ErrorState, LoadingState } from '../components/States';
import { ThreadHeader, UNTITLED_THREAD } from '../components/threads/ThreadHeader';
import { ThreadMessages } from '../components/threads/ThreadMessages';
import { ThreadSummaryCard } from '../components/threads/ThreadSummaryCard';
import { ThreadProposalsCard } from '../components/threads/ThreadProposalsCard';

/**
 * One thread: the discussion in the main column, "Where we are" and the
 * discovered-issues ledger on a sticky rail (stacked above the discussion as
 * collapsible sections below `lg`).
 */
export function ThreadPage() {
  const { projectId = '', threadId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { wp } = useWorkspacePath();
  const { can } = useCapabilities();
  const threadsOn = useFeature('chat');
  const stacked = useIsMobile('(max-width: 1023px)');
  const [actionError, setActionError] = useState<string | null>(null);

  const projectQuery = useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => fetchProject(projectId),
    enabled: !!projectId,
  });
  useCanonicalProjectRef(projectId, projectQuery.data?.code);
  const project = projectQuery.data;
  const resolvedId = project?.id ?? '';
  const projectRef = project ? (project.code ?? project.id) : projectId;
  const base = project ? threadsHref(project.kind, projectRef) : '';

  const threadQuery = useQuery({
    queryKey: qk.thread(resolvedId, threadId),
    queryFn: () => fetchThread(resolvedId, threadId),
    enabled: !!resolvedId && !!threadId,
  });
  const thread = threadQuery.data;

  const onPmMessage = useCallback(
    (message: ThreadMessageDto) => {
      maybeNotifyPmMessage(message, {
        activeThreadId: threadId,
        projectName: project?.name,
        href: wp(`${base}/${message.threadId}`),
        onNavigate: (href) => navigate(href),
      });
    },
    [base, navigate, project?.name, threadId, wp],
  );
  const { thinkingThreadId, failedThreadId, subscribeFailed } = useThreadSocket(
    resolvedId || undefined,
    onPmMessage,
  );

  const canPost = !!project && can('thread:post', { projectId: project.id }) && threadsOn !== false;
  const canFile = !!project && can('issue:create', { projectId: project.id }) && threadsOn !== false;

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: qk.threads(resolvedId) });

  const updateMutation = useMutation({
    mutationFn: (patch: { title?: string; resolved?: boolean }) => updateThread(resolvedId, threadId, patch),
    onMutate: () => setActionError(null),
    onSuccess: invalidate,
    onError: (err) => setActionError(errorMessage(err, 'Could not update the thread')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteThread(resolvedId, threadId),
    onMutate: () => setActionError(null),
    onSuccess: () => {
      invalidate();
      navigate(wp(base), { replace: true });
    },
    onError: (err) => setActionError(errorMessage(err, 'Could not delete the thread')),
  });

  useDocumentTitle(
    project && thread ? `${thread.title?.trim() || UNTITLED_THREAD} · ${project.name}` : project ? `Thread · ${project.name}` : 'Thread',
  );

  if (projectQuery.isLoading || (!!resolvedId && threadQuery.isLoading)) {
    return <LoadingState label="Loading thread…" />;
  }
  if (projectQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(projectQuery.error, 'Failed to load project')}
        onRetry={() => projectQuery.refetch()}
      />
    );
  }
  if (threadQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(threadQuery.error, 'Failed to load thread')}
        onRetry={() => threadQuery.refetch()}
      />
    );
  }
  if (!project || !thread) return <LoadingState label="Loading thread…" />;

  const nav = projectNav(project);
  const showThinking = thinkingThreadId === '*' || thinkingThreadId === threadId;
  const showFailed = failedThreadId === '*' || failedThreadId === threadId;
  const readOnlyMessage = threadsOn === false ? featureDisabledMessage('chat') : undefined;

  const rail = (
    <>
      <ThreadSummaryCard
        summary={thread.summary}
        summaryUpdatedAt={thread.summaryUpdatedAt}
        collapsible={stacked}
      />
      <ThreadProposalsCard
        projectId={project.id}
        projectRef={projectRef}
        projectKind={project.kind}
        threadId={threadId}
        proposals={thread.proposals}
        canFile={canFile}
        canDecide={canPost}
        collapsible={stacked}
      />
    </>
  );

  return (
    <div className="space-y-4 pb-16 sm:pb-0">
      <Breadcrumbs
        items={[
          { label: nav.rootLabel, to: wp(nav.rootTo) },
          { label: nav.name, to: wp(nav.selfTo) },
          { label: 'Threads', to: wp(base) },
          { label: thread.title?.trim() || UNTITLED_THREAD },
        ]}
      />

      <ThreadHeader
        thread={thread}
        canEdit={canPost}
        busy={updateMutation.isPending || deleteMutation.isPending}
        deleteError={deleteMutation.isError ? errorMessage(deleteMutation.error, 'Could not delete the thread') : null}
        onRename={(title) => updateMutation.mutate({ title })}
        onToggleResolved={() => updateMutation.mutate({ resolved: thread.resolvedAt === null })}
        onDelete={() => deleteMutation.mutateAsync().then(() => undefined)}
      />

      {actionError && !deleteMutation.isError && <Callout tone="danger">{actionError}</Callout>}
      {subscribeFailed && <Callout tone="danger">Live updates unavailable — refresh to retry.</Callout>}

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-6">
        {stacked && <div className="mb-4 space-y-3">{rail}</div>}

        <div className="flex h-[calc(100dvh-20rem)] min-h-[420px] flex-col">
          <ThreadMessages
            projectId={project.id}
            threadId={threadId}
            readOnly={!canPost}
            readOnlyMessage={readOnlyMessage}
            isPmThinking={showThinking}
            lastTurnFailed={showFailed}
          />
        </div>

        {!stacked && <aside className="space-y-4 lg:sticky lg:top-4">{rail}</aside>}
      </div>
    </div>
  );
}
